import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { esES } from '@mui/material/locale';
import { esES as gridEsES } from '@mui/x-data-grid';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';

// Paleta "editorial esmeralda": papel cálido, tinta, acento esmeralda. Estilo Linear/Vercel.
const getDesignTokens = (mode) => {
  const dark = mode === 'dark';
  const border = dark ? 'rgba(255,255,255,0.10)' : 'rgba(20,32,28,0.10)';
  const borderHover = dark ? 'rgba(45,212,167,0.45)' : 'rgba(14,124,102,0.45)';
  return {
  palette: {
    mode,
    primary: {
      main: dark ? '#2dd4a7' : '#0e7c66',
      light: '#1d9e75',
      dark: '#0b5e4d',
      contrastText: dark ? '#06241d' : '#ffffff',
    },
    secondary: {
      main: '#c2410c',
      light: '#ea580c',
      dark: '#9a3412',
      contrastText: '#ffffff',
    },
    ...(dark
      ? {
          background: { default: '#0f1311', paper: '#161a18' },
          text: { primary: '#f1f4f2', secondary: '#9aa39d' },
          divider: border,
        }
      : {
          background: { default: '#fcfcfa', paper: '#ffffff' },
          text: { primary: '#16201c', secondary: '#5c635f' },
          divider: border,
        }),
    success: { main: '#0e7c66', light: '#1d9e75', dark: '#0b5e4d' },
    warning: { main: '#b45309', light: '#d97706', dark: '#92400e' },
    error: { main: '#b91c1c', light: '#dc2626', dark: '#991b1b' },
    info: { main: '#185fa5', light: '#2078c4', dark: '#0c447c' },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
    h1: { fontWeight: 600, fontSize: '2.75rem', lineHeight: 1.1, letterSpacing: '-0.03em' },
    h2: { fontWeight: 600, fontSize: '2.125rem', lineHeight: 1.15, letterSpacing: '-0.025em' },
    h3: { fontWeight: 600, fontSize: '1.625rem', lineHeight: 1.25, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, fontSize: '1.3rem', lineHeight: 1.3, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.4, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.45, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, lineHeight: 1.5 },
    body1: { fontWeight: 400, lineHeight: 1.65 },
    body2: { fontWeight: 400, lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    overline: { fontWeight: 600, letterSpacing: '0.12em', fontSize: '0.72rem' },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: dark ? '#0f1311' : '#fcfcfa' },
        '::selection': { background: dark ? 'rgba(45,212,167,0.30)' : 'rgba(14,124,102,0.18)' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '9px 18px',
          fontSize: '0.9375rem',
          boxShadow: 'none',
          transition: 'background-color .2s ease, border-color .2s ease, color .2s ease',
          '&:hover': { boxShadow: 'none' },
        },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        outlined: { borderColor: border },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `0.5px solid ${border}`,
          boxShadow: 'none',
          backgroundImage: 'none',
          transition: 'border-color .2s ease, background-color .2s ease',
          '&:hover': { borderColor: borderHover },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500 },
        outlined: { borderColor: border },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 14, backgroundImage: 'none' },
        outlined: { border: `0.5px solid ${border}` },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: dark ? 'rgba(15,19,17,0.85)' : 'rgba(252,252,250,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          boxShadow: 'none',
          borderBottom: `0.5px solid ${border}`,
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: border } } },
  },
  };
};

function AppWithTheme() {
  const { mode } = useThemeMode();
  // textos de los componentes en español (paginación, tablas, selección): «Filas por página», no «Rows per page»
  const theme = useMemo(() => createTheme(getDesignTokens(mode), gridEsES, esES), [mode]);
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </MuiThemeProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AppWithTheme />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);